import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  UsersIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyEuroIcon,
  UserIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ArrowsRightLeftIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import useNetworkHierarchy from '../../hooks/useNetworkHierarchy';

const CostruttoreRete = ({
  rete,
  nodoSelezionato,
  onNodoSelezionato,
  onAggiungiNodo,
  onModificaNodo,
  onRimuoviNodo,
  onSpostaNodo,
  onCambiaGrado,
  onGestisciTemplate,
  tipiNodo,
  user
}) => {
  const { canViewNode, canManageNode, getVisibleNodeIds, userNode } = useNetworkHierarchy(user);
  const ruolo = user?.role || user?.ruolo || '';
  const [nodiEspansi, setNodiEspansi] = useState(new Set());
  const [gradoMenuNodoId, setGradoMenuNodoId] = useState(null);
  // Gradi assegnabili rapidamente (solo ruoli "persona")
  const GRADI_RAPIDI = (tipiNodo || []).filter(t => t.id === 'agente' || t.id === 'team_leader');
  const [alberoRete, setAlberoRete] = useState(null);

  const [alberoInizializzato, setAlberoInizializzato] = useState(false);

  // Costruisce l'albero gerarchico dai dati piatti
  useEffect(() => {
    if (rete?.nodi) {
      let albero = costruisciAlbero(rete.nodi);
      // Filtra l'albero in base al ruolo
      if (user && userNode) {
        const ruolo = user.role || user.ruolo || '';
        if (ruolo === 'agenzia') {
          const radiceAgenzia = albero.find(n => n.id === userNode.id);
          albero = radiceAgenzia ? [radiceAgenzia] : [];
        } else if (ruolo === 'agente' || ruolo === 'subagente' || ruolo === 'agente_commerciale') {
          const agenziaPrimaria = albero.find(n => n.tipo === 'agenzia_primaria');
          let agentiDaMostrare = [];
          if (agenziaPrimaria) {
            const trovaNodo = (nodo) => {
              if (nodo.id === userNode.id) return nodo;
              for (const figlio of nodo.figli || []) {
                const trovato = trovaNodo(figlio);
                if (trovato) return trovato;
              }
              return null;
            };
            const nodoAgente = trovaNodo(agenziaPrimaria);
            if (nodoAgente) {
              const agenziaClone = { ...agenziaPrimaria, figli: [ { ...nodoAgente } ] };
              agentiDaMostrare = [agenziaClone];
            } else {
              agentiDaMostrare = [agenziaPrimaria];
            }
          }
          albero = agentiDaMostrare;
        }
      }
      setAlberoRete(albero);
      if (!alberoInizializzato && albero.length > 0) {
        setNodiEspansi(new Set([albero[0].id]));
        setAlberoInizializzato(true);
      }
    }
  }, [rete, user?.id, userNode?.id]);

  const costruisciAlbero = (nodi) => {
    const mappa = {};
    const radici = [];

    // Crea una mappa di tutti i nodi
    nodi.forEach(nodo => {
      mappa[nodo.id] = { ...nodo, figli: [] };
    });

    // Costruisce la gerarchia
    nodi.forEach(nodo => {
      if (nodo.parentId) {
        if (mappa[nodo.parentId]) {
          mappa[nodo.parentId].figli.push(mappa[nodo.id]);
        }
      } else {
        radici.push(mappa[nodo.id]);
      }
    });

    return radici;
  };

  const toggleEspansione = (nodoId) => {
    const nuoviEspansi = new Set(nodiEspansi);
    if (nuoviEspansi.has(nodoId)) {
      nuoviEspansi.delete(nodoId);
    } else {
      nuoviEspansi.add(nodoId);
    }
    setNodiEspansi(nuoviEspansi);
  };

  const getTipoNodo = (tipo) => {
    return tipiNodo.find(t => t.id === tipo) || tipiNodo[2];
  };

  const calcolaStatisticheNodo = (nodo) => {
    const conteggioFigli = nodo.figli?.length || 0;
    const conteggioDiscendenti = contaDiscendenti(nodo);
    const livelloMassimo = calcolaLivelloMassimo(nodo);
    
    return {
      figli: conteggioFigli,
      discendenti: conteggioDiscendenti,
      livelloMassimo
    };
  };

  const contaDiscendenti = (nodo) => {
    let count = nodo.figli?.length || 0;
    nodo.figli?.forEach(figlio => {
      count += contaDiscendenti(figlio);
    });
    return count;
  };

  const calcolaLivelloMassimo = (nodo, livelloCorrente = 0) => {
    if (!nodo.figli || nodo.figli.length === 0) {
      return livelloCorrente;
    }
    
    return Math.max(
      ...nodo.figli.map(figlio => 
        calcolaLivelloMassimo(figlio, livelloCorrente + 1)
      )
    );
  };

  // Funzione robusta per determinare il ruolo
  const getRuoloUtente = () => (user?.role || user?.ruolo || '').toLowerCase();
  const ruoloUtente = getRuoloUtente();
  const isAdmin = ruoloUtente === 'admin';

  const renderNodo = (nodo, livello = 0, isUltimo = false, path = []) => {
    // Nascondi i nodi che l'utente non può vedere
    if (!canViewNode(nodo.id)) return null;
    const tipoNodo = getTipoNodo(nodo.tipo);
    const IconComponent = tipoNodo.icona;
    const isSelezionato = nodoSelezionato?.id === nodo.id;
    const isEspanso = nodiEspansi.has(nodo.id);
    const haFigli = nodo.figli && nodo.figli.length > 0;
    const statistiche = calcolaStatisticheNodo(nodo);
    const nuovoPath = [...path, nodo.id];
    // --- LOGICA VISIBILITÀ/DETTAGLI/AZIONI ---
    // Admin: tutto visibile e tutte le azioni
    // Agenzia: vede solo se stessa e la rete a scendere, tutti i dettagli
    // Agente: vede solo se stesso e chi ha sopra, ma solo i dettagli del proprio nodo
    const isAgenzia = ruoloUtente === 'agenzia';
    const isAgente = ruoloUtente === 'agente' || ruoloUtente === 'subagente' || ruoloUtente === 'agente_commerciale';
    // Dettagli:
    // - Admin: sempre
    // - Agenzia: sempre per i nodi visibili
    // - Agente: solo per il proprio nodo
    let showDetails = true;
    if (isAgente) {
      showDetails = userNode && nodo.id === userNode.id;
    }
    // Per agenti e agenzie: mostra solo il nome del nodo e tipo, nessun dettaglio, nessuna azione
    const hideDetails = (isAgente || isAgenzia) && userNode && getVisibleNodeIds().includes(nodo.id);
    const hideActions = !isAdmin;
    // Per agenzia: mostra dettagli ma nessuna azione
    const hideActionsAgenzia = isAgenzia && userNode && (nodo.id === userNode.id || getVisibleNodeIds().includes(nodo.id));

    return (
      <div key={nodo.id} className={`rete-nodo-container ${livello > 0 ? 'ml-8' : ''}`}>
        {/* Linee di collegamento per livelli superiori */}
        {livello > 0 && (
          <div className="relative">
            {/* Linea verticale dal genitore */}
            <div 
              className="absolute w-px bg-gray-300 -left-4 top-0 h-4"
              style={{ left: '-32px' }}
            ></div>
            {/* Linea orizzontale verso il nodo */}
            <div 
              className="absolute h-px bg-gray-300 top-4 w-8"
              style={{ left: '-32px' }}
            ></div>
          </div>
        )}

        {/* Card del nodo */}
        <div 
          className={`
            p-6 hover:bg-gray-50 transition-colors border-b border-gray-200
            ${isSelezionato ? 'bg-blue-50' : ''}
          `}
          onClick={() => onNodoSelezionato(nodo)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-4">
                {/* Icona espansione se ha figli */}
                {haFigli && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEspansione(nodo.id);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {isEspanso ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                )}

                {/* Info principale */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {nodo.tipo === 'agenzia_primaria' 
                      ? nodo.nome 
                      : nodo.utente 
                        ? `${tipoNodo.nome} ${nodo.utente.nome} ${nodo.utente.cognome}`
                        : nodo.nome
                    }
                  </h3>
                  <p className="text-sm text-gray-500">ID #{nodo.id}</p>
                  {/* Email sotto l'ID se presente e non agente */}
                  {nodo.utente && !hideDetails && (
                    <p className="text-xs text-gray-400 truncate max-w-48">
                      {nodo.utente.email}
                    </p>
                  )}
                </div>

                {/* Tipo nodo badge */}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-${tipoNodo.colore}-50 text-${tipoNodo.colore}-700 border-${tipoNodo.colore}-200`}>
                  {tipoNodo.nome}
                </span>

                {/* Provvigione */}
                {!hideDetails && (
                <div className="text-sm font-medium text-green-600">
                  {nodo.tipo === 'sito_vetrina' 
                    ? 'Automatica'
                    : nodo.provvigioneTipo === 'percentuale' 
                    ? `${nodo.provvigioneValore || 0}%`
                    : `€${nodo.provvigioneValore || 0}`
                  }
                </div>
                )}
              </div>

              {/* Dettagli del nodo: secondo la logica sopra */}
              {showDetails && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                {/* Utente - senza email ora */}
                {nodo.utente && (
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {nodo.tipo === 'agenzia_primaria' 
                          ? `${nodo.utente.nome} ${nodo.utente.cognome}`
                          : nodo.utente.username
                        }
                      </p>
                      <p className="text-gray-500">Utente assegnato</p>
                    </div>
                  </div>
                )}

                {/* Livello */}
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">Livello {nodo.livello}</p>
                    <p className="text-gray-500">Posizione gerarchica</p>
                  </div>
                </div>

                {/* Statistiche figli */}
                {statistiche.figli > 0 && (
                  <div className="flex items-center space-x-2">
                    <UsersIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">{statistiche.figli} figli</p>
                      <p className="text-gray-500">{statistiche.discendenti} totali</p>
                    </div>
                  </div>
                )}

                {/* Provvigione dettaglio */}
                {nodo.tipo !== 'sito_vetrina' && (
                <div>
                  <p className="font-medium text-gray-900">
                    {nodo.provvigioneTipo === 'percentuale' ? 'Percentuale' : 'Fissa'}
                  </p>
                  <p className="text-gray-500">Tipo provvigione</p>
                </div>
                )}

                {/* Info specifiche per Sito Vetrina */}
                {nodo.tipo === 'sito_vetrina' && (
                  <>
                    {nodo.fonte && (
                      <div className="flex items-center space-x-2">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{nodo.fonte.descrizione}</p>
                          <p className="text-gray-500">Fonte provenienza</p>
                        </div>
                      </div>
                    )}
                    
                    {nodo.dettaglioProvenienza && (
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{nodo.dettaglioProvenienza.descrizione}</p>
                          <p className="text-gray-500">Dettaglio provenienza</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              )}

              {/* Descrizione se presente: secondo la logica sopra */}
              {showDetails && nodo.descrizione && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{nodo.descrizione}</p>
                </div>
              )}
            </div>

            {/* Azioni: solo per admin, sia visibilità che attivazione */}
            {isAdmin && !hideDetails && (
            <div className="ml-6">
              <div className="flex items-center space-x-2">
                {/* Branding per agenzie primarie */}
                {nodo.tipo === 'agenzia_primaria' && onGestisciTemplate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                        if (isAdmin) onGestisciTemplate(nodo);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                    Branding
                  </button>
                )}
                  {/* Bottone Aggiungi Nodo: SOLO admin, controllo doppio a prova di errore */}
                  {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                        if (isAdmin) onAggiungiNodo(nodo);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Aggiungi
                </button>
                  )}
                  {/* Modifica: solo admin */}
                  {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                        if (isAdmin) onModificaNodo(nodo);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Modifica
                </button>
                  )}
                  {/* Sposta nella rete (non per agenzie primarie = radici): solo admin */}
                  {isAdmin && nodo.tipo !== 'agenzia_primaria' && onSpostaNodo && (
                  <button
                    onClick={(e) => { e.stopPropagation(); if (isAdmin) onSpostaNodo(nodo); }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4 mr-2" />
                    Sposta
                  </button>
                  )}
                  {/* Cambia grado (solo agente/team leader): solo admin */}
                  {isAdmin && onCambiaGrado && (nodo.tipo === 'agente' || nodo.tipo === 'team_leader') && GRADI_RAPIDI.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setGradoMenuNodoId(gradoMenuNodoId === nodo.id ? null : nodo.id); }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <AcademicCapIcon className="h-4 w-4 mr-2" />
                      Grado
                      <ChevronDownIcon className="h-4 w-4 ml-1" />
                    </button>
                    {gradoMenuNodoId === nodo.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setGradoMenuNodoId(null); }} />
                        <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-xl ring-1 ring-gray-200 z-20 py-1">
                          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Cambia grado in…</div>
                          {GRADI_RAPIDI.map((g) => (
                            <button
                              key={g.id}
                              disabled={g.id === nodo.tipo}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGradoMenuNodoId(null);
                                if (g.id !== nodo.tipo) onCambiaGrado(nodo, g.id);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${g.id === nodo.tipo ? 'text-gray-400 cursor-default bg-gray-50' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`}
                            >
                              <AcademicCapIcon className="h-4 w-4" />
                              {g.nome}
                              {g.id === nodo.tipo && <span className="ml-auto text-[10px] text-gray-400">attuale</span>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  )}
                  {/* Rimuovi (non per agenzie primarie): solo admin */}
                  {isAdmin && nodo.tipo !== 'agenzia_primaria' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                        if (isAdmin) onRimuoviNodo(nodo.id);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Rendering nodi figli */}
        {haFigli && isEspanso && (
          <div className="ml-4 relative">
            {/* Linea verticale per collegare i figli */}
            <div 
              className="absolute w-px bg-gray-300 left-0 top-0"
              style={{ 
                height: `${nodo.figli.length * 120}px`, // Altezza approssimativa
                left: '12px'
              }}
            ></div>
            
            {nodo.figli.map((figlio, index) => 
              renderNodo(figlio, livello + 1, index === nodo.figli.length - 1, nuovoPath)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStatisticheGenerali = () => {
    if (!rete?.nodi?.length) return null;

    // RIMOSSO: badge riepilogo
    return null;
  };

  const renderContenutoRete = () => {
    if (!alberoRete || alberoRete.length === 0) {
      return (
        <div className="p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0V9a2 2 0 012-2h2a2 2 0 012 2v12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun nodo nella rete</h3>
          <p className="text-sm text-gray-500">
            Inizia aggiungendo un'Agenzia Primaria come nodo radice per costruire la tua rete.
          </p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200">
        {alberoRete.map(nodo => renderNodo(nodo))}
      </div>
    );
  };

  return (
    <div className="rete-costruttore">
      {renderStatisticheGenerali()}
      
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Struttura Gerarchica
          </h3>
          
          <div className="text-sm text-gray-500">
            💡 Clicca su un nodo per visualizzare i dettagli, usa i pulsanti per gestire la rete
          </div>
        </div>
      </div>
      
      {renderContenutoRete()}
    </div>
  );
};

export default CostruttoreRete; 